// "Gebäude verwalten" dialog — buildings with an ordered list of floors.
import { api } from "../api";
import { confirmDialog } from "./prompt";
import type { Building, Floor } from "../types";

const esc = (s: string): string =>
  (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

let dlg: HTMLDialogElement | null = null;
let notify: () => void = () => {};
let buildings: Building[] = [];

export async function openBuildingManager(onChange: () => void): Promise<void> {
  notify = onChange;
  if (!dlg) {
    dlg = document.createElement("dialog");
    dlg.className = "dialog dialog-wide";
    dlg.innerHTML = `
      <div class="dialog-body">
        <div class="dialog-head">
          <h2>Gebäude &amp; Stockwerke</h2>
          <button type="button" class="btn ghost sm" data-a="close">Schließen</button>
        </div>
        <div data-list="buildings"></div>
        <div class="cat-add">
          <input type="text" data-f="newBuilding" placeholder="Neues Gebäude…" />
          <button type="button" class="btn sm primary" data-a="addBuilding">Anlegen</button>
        </div>
      </div>`;
    document.body.appendChild(dlg);
    dlg.querySelector('[data-a="close"]')!.addEventListener("click", () => dlg!.close());
    dlg.querySelector('[data-a="addBuilding"]')!.addEventListener("click", async () => {
      const input = dlg!.querySelector<HTMLInputElement>('[data-f="newBuilding"]')!;
      const name = input.value.trim();
      if (!name) return;
      await api.createBuilding(name);
      input.value = "";
      await refresh();
    });
  }
  await refresh();
  dlg.showModal();
}

async function refresh(): Promise<void> {
  buildings = await api.listBuildings();
  render();
  notify();
}

function render(): void {
  const host = dlg!.querySelector<HTMLElement>('[data-list="buildings"]')!;
  host.replaceChildren();
  if (!buildings.length) {
    host.innerHTML = '<div class="empty-note">Noch keine Gebäude angelegt.</div>';
    return;
  }
  for (const b of buildings) host.appendChild(buildingBlock(b));
}

function buildingBlock(b: Building): HTMLElement {
  const box = document.createElement("div");
  box.className = "bld-block";

  const head = document.createElement("div");
  head.className = "bld-head";
  head.innerHTML = `
    <input type="text" class="cat-inline" data-f="name" value="${esc(b.name)}" />
    <button type="button" class="btn sm ghost" data-a="rename">Umbenennen</button>
    <button type="button" class="btn sm ghost" data-a="del" style="color:var(--danger)">Löschen</button>`;
  head.querySelector('[data-a="rename"]')!.addEventListener("click", async () => {
    const name = head.querySelector<HTMLInputElement>('[data-f="name"]')!.value.trim();
    if (name && name !== b.name) {
      await api.renameBuilding(b.id, name);
      await refresh();
    }
  });
  head.querySelector('[data-a="del"]')!.addEventListener("click", async () => {
    const ok = await confirmDialog(
      "Gebäude löschen",
      `„${b.name}" mit ${b.floors.length} Stockwerk(en) löschen? Zugeordnete Foyers bleiben erhalten, verlieren aber die Zuordnung.`,
      { confirmLabel: "Löschen" }
    );
    if (!ok) return;
    await api.deleteBuilding(b.id);
    await refresh();
  });
  box.appendChild(head);

  const floors = document.createElement("div");
  floors.className = "bld-floors";
  b.floors.forEach((f, i) => floors.appendChild(floorRow(b, f, i)));
  box.appendChild(floors);

  const add = document.createElement("div");
  add.className = "cat-add";
  add.innerHTML = `
    <input type="text" data-f="floor" placeholder="Stockwerk… (z. B. 2. OG)" />
    <button type="button" class="btn sm" data-a="addFloor">+ Stockwerk</button>`;
  add.querySelector('[data-a="addFloor"]')!.addEventListener("click", async () => {
    const name = add.querySelector<HTMLInputElement>('[data-f="floor"]')!.value.trim();
    if (!name) return;
    await api.addFloor(b.id, name);
    await refresh();
  });
  box.appendChild(add);
  return box;
}

function floorRow(b: Building, f: Floor, index: number): HTMLElement {
  const row = document.createElement("div");
  row.className = "bld-floor-row";
  row.innerHTML = `
    <button type="button" class="btn icon ghost sm" data-a="up" title="Nach oben" ${index === 0 ? "disabled" : ""}>↑</button>
    <button type="button" class="btn icon ghost sm" data-a="down" title="Nach unten" ${index === b.floors.length - 1 ? "disabled" : ""}>↓</button>
    <input type="text" class="cat-inline" data-f="name" value="${esc(f.name)}" />
    <button type="button" class="btn sm ghost" data-a="save">Speichern</button>
    <button type="button" class="btn sm ghost" data-a="del" style="color:var(--danger)">×</button>`;

  const swap = async (dir: -1 | 1) => {
    const other = b.floors[index + dir];
    if (!other) return;
    await api.updateFloor(f.id, { sortOrder: other.sortOrder });
    await api.updateFloor(other.id, { sortOrder: f.sortOrder });
    await refresh();
  };
  row.querySelector('[data-a="up"]')!.addEventListener("click", () => swap(-1));
  row.querySelector('[data-a="down"]')!.addEventListener("click", () => swap(1));
  row.querySelector('[data-a="save"]')!.addEventListener("click", async () => {
    const name = row.querySelector<HTMLInputElement>('[data-f="name"]')!.value.trim();
    if (name && name !== f.name) {
      await api.updateFloor(f.id, { name });
      await refresh();
    }
  });
  row.querySelector('[data-a="del"]')!.addEventListener("click", async () => {
    await api.deleteFloor(f.id);
    await refresh();
  });
  return row;
}
